import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import PageShell from "../dashboard/PageShell";
import {
  Plus,
  Search,
  FileDown,
  ChevronDown,
  Eye,
  Copy,
  Pencil,
  Trash2,
  Users,
  DollarSign,
  FileSignature,
  Share2,
} from "lucide-react";

interface ProjectItem {
  id: string;
  stt: number;
  function: string;
  projectName: string;
  status: string;
  startDate: string;
  endDate: string;
  managementLevel: string;
  projectType: string;
  projectLeader: string;
  leadUnit: string;
  budget: string;
  hashId: string;
}

const mockData: ProjectItem[] = [
  {
    id: "1",
    stt: 1,
    function: "- Chọn -",
    projectName: "Nghiên cứu phần mềm quản lý KHCN có ứng dụng AI »",
    status: "Nhận hồ sơ thuyết minh",
    startDate: "12/2025",
    endDate: "12/2025",
    managementLevel: "Cấp Viện/ Chi nhánh Tỉnh/ TP: Hà Nội",
    projectType: "Đề tài",
    projectLeader: "Thượng tá, VS. ThS Lê Anh Tuấn",
    leadUnit: "Trung tâm Đào tạo và Hợp tác Quốc tế",
    budget: "10.000",
    hashId: "2020",
  },
  {
    id: "2",
    stt: 2,
    function: "- Chọn -",
    projectName: "NC »",
    status: "Phê duyệt danh mục thực hiện",
    startDate: "10/2025",
    endDate: "06/2025",
    managementLevel: "Cấp Quốc gia",
    projectType: "Đề tài độc lập",
    projectLeader: "Thượng tá, VS. ThS Lê Anh Tuấn",
    leadUnit: "Viện Khoa học Thủy lợi Miền Nam",
    budget: "",
    hashId: "2019",
  },
  {
    id: "3",
    stt: 3,
    function: "- Chọn -",
    projectName: "Nghiên cứu phần mềm quản lý KHCN có ứng dụng AI »",
    status: "Nhận hồ sơ thuyết minh",
    startDate: "12/2025",
    endDate: "12/2025",
    managementLevel: "Cấp Viện/ Chi nhánh Tỉnh/ TP: Hà Nội",
    projectType: "Đề tài",
    projectLeader: "Thượng tá, VS. ThS Lê Anh Tuấn",
    leadUnit: "Trung tâm Đào tạo và Hợp tác Quốc tế",
    budget: "10.000",
    hashId: "2017",
  },
  {
    id: "4",
    stt: 4,
    function: "- Chọn -",
    projectName: "Nghiên cứu phần mềm quản lý KHCN có ứng dụng AI »",
    status: "Nhận hồ sơ thuyết minh",
    startDate: "12/2025",
    endDate: "12/2025",
    managementLevel: "Cấp Viện/ Chi nhánh Tỉnh/ TP: Hà Nội",
    projectType: "Đề tài",
    projectLeader: "Thượng tá, VS. ThS Lê Anh Tuấn",
    leadUnit: "Trung tâm Đào tạo và Hợp tác Quốc tế",
    budget: "10.000",
    hashId: "2016",
  },
  {
    id: "5",
    stt: 5,
    function: "- Chọn -",
    projectName:
      "Điều tra, nghiên cứu, đánh giá thực trạng và nguy cơ lũ lụt, sạt lở đất lưu vực sông Sê Sanx và đề xuất các giải pháp phòng tránh và giải nhẹ »",
    status: "Phê duyệt danh mục thực hiện",
    startDate: "01/2022",
    endDate: "12/2022",
    managementLevel: "Cấp Bộ",
    projectType: "Đề tài",
    projectLeader: "Thượng tá, GS. TS Nguyễn Thị Hạnh",
    leadUnit: "Viện Khoa học Thủy lợi Miền Trung và Tây Nguyên",
    budget: "200",
    hashId: "1016",
  },
];

const TopicApproval: React.FC = () => {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [openMenuProjectId, setOpenMenuProjectId] = useState<string | null>(
    null
  );
  const [menuPlacement, setMenuPlacement] = useState<"bottom" | "top">(
    "bottom"
  );
  const [buttonPosition, setButtonPosition] = useState<{
    left: number;
    top: number;
    right: number;
    bottom: number;
  } | null>(null);
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Delete modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteProjectId, setDeleteProjectId] = useState<string | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuProjectId) {
        const currentRef = dropdownRefs.current[openMenuProjectId];
        if (currentRef && !currentRef.contains(event.target as Node)) {
          setOpenMenuProjectId(null);
        }
      }
    };

    const handleScroll = () => {
      if (openMenuProjectId && buttonPosition) {
        // Update position on scroll to keep menu attached to button
        const button = document.querySelector(
          `[data-project-id="${openMenuProjectId}"]`
        ) as HTMLButtonElement;
        if (button) {
          const rect = button.getBoundingClientRect();
          setButtonPosition({
            left: rect.left,
            top: rect.top,
            right: rect.right,
            bottom: rect.bottom,
          });
        }
      }
    };

    if (openMenuProjectId) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [openMenuProjectId, buttonPosition]);

  return (
    <PageShell title="Danh mục xét duyệt thuyết minh">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Danh mục xét duyệt thuyết minh
          </h1>
          <button
            onClick={() => navigate("/add-new-project")}
            className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Thêm mới Dự án</span>
          </button>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg p-4">
          <div className="space-y-4">
            {/* Row 1 - Search input + Search button */}
            <div className="flex items-center space-x-4">
              <div className="relative w-96">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Tìm kiếm dự án"
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors">
                Tìm kiếm
              </button>
            </div>

            {/* Row 2 - 6 filter dropdowns */}
            <div className="grid grid-cols-6 gap-4">
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Đơn vị chủ trì -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Chọn cấp -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Dạng đề tài -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Lĩnh vực -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Trạng thái -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Chủ nhiệm -</option>
              </select>
            </div>

            {/* Row 3 - 6 filter dropdowns */}
            <div className="grid grid-cols-6 gap-4">
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Thư ký -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Lọc theo -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Từ năm -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Đến năm -</option>
              </select>
              <div></div>
              <div></div>
            </div>

            {/* Row 4 - Export button */}
            <div className="flex justify-start">
              <button className="bg-orange-500 text-white px-3 py-2 rounded text-sm font-medium hover:bg-orange-600 transition-colors flex items-center space-x-2">
                <FileDown className="h-4 w-4" />
                <span>Xuất ra file</span>
              </button>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow">
          <div className="overflow-x-auto overflow-y-visible">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                    STT
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                    Tên đề tài, nhiệm vụ
                  </th>
                  <th
                    className="px-4 py-3 text-center text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200"
                    colSpan={2}
                  >
                    Thời gian thực hiện
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                    Cấp nhiệm vụ
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                    Chủ nhiệm nhiệm vụ
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                    Đơn vị chủ trì thực hiện
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                    Kinh phí (triệu đồng)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                    Trạng thái
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                    #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider">
                    Chức năng
                  </th>
                </tr>
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-2 border-r border-gray-200"></th>
                  <th className="px-4 py-2 border-r border-gray-200"></th>
                  <th className="px-4 py-2 text-center text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                    Bắt đầu
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                    Kết thúc
                  </th>
                  <th className="px-4 py-2 border-r border-gray-200"></th>
                  <th className="px-4 py-2 border-r border-gray-200"></th>
                  <th className="px-4 py-2 border-r border-gray-200"></th>
                  <th className="px-4 py-2 border-r border-gray-200"></th>
                  <th className="px-4 py-2 border-r border-gray-200"></th>
                  <th className="px-4 py-2 border-r border-gray-200"></th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mockData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">
                      {item.stt}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900 max-w-lg border-r border-gray-200">
                      <div>
                        <button
                          onClick={() => {
                            localStorage.setItem(
                              "projectListActive",
                              "approval"
                            );
                            navigate(`/topics/approval/detail/${item.id}`);
                          }}
                          className="font-medium text-[#337ab7] text-[14px] hover:underline bg-transparent border-none p-0 cursor-pointer"
                          style={{ textAlign: "left" }}
                        >
                          {item.projectName}
                        </button>
                      </div>
                      <div className="text-sm text-gray-600">
                        Trạng thái:{" "}
                        <span
                          className={
                            item.status === "Nhận hồ sơ thuyết minh" ||
                            item.status === "Phê duyệt danh mục thực hiện"
                              ? "text-[#31708f]"
                              : ""
                          }
                        >
                          {item.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900 text-center border-r border-gray-200">
                      {item.startDate}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900 text-center border-r border-gray-200">
                      {item.endDate}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900 border-r border-gray-200">
                      <div className="space-y-0.5">
                        <div className="leading-tight text-sm">
                          {item.managementLevel}
                        </div>
                        {item.projectType && (
                          <div className="text-sm text-gray-600 leading-tight">
                            Dạng: {item.projectType}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900 border-r border-gray-200">
                      {item.projectLeader}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900 max-w-xs border-r border-gray-200">
                      {item.leadUnit}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900 text-right border-r border-gray-200 w-24">
                      {item.budget ? (
                        <span className="text-red-600 font-semibold">
                          {item.budget}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900 border-r border-gray-200">
                      {item.status}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900 border-r border-gray-200">
                      {item.hashId}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap border-r border-gray-200 w-20">
                      <div
                        className="relative inline-block"
                        ref={(el) => {
                          dropdownRefs.current[item.id] = el;
                        }}
                      >
                        <button
                          data-project-id={item.id}
                          className="bg-[#337ab7] text-white px-2 py-1 rounded text-xs font-medium hover:bg-[#2e6da4] transition-colors flex items-center space-x-1"
                          onClick={(e) => {
                            const target = e.currentTarget as HTMLButtonElement;
                            const rect = target.getBoundingClientRect();
                            const estimatedMenuHeight = 280; // approx height for 8 items with padding
                            const spaceBelow = window.innerHeight - rect.bottom;
                            // More aggressive flipping - flip if less than 300px space below
                            const willOverflowBottom = spaceBelow < 300;
                            setMenuPlacement(
                              willOverflowBottom ? "top" : "bottom"
                            );
                            setButtonPosition({
                              left: rect.left,
                              top: rect.top,
                              right: rect.right,
                              bottom: rect.bottom,
                            });
                            setOpenMenuProjectId(
                              openMenuProjectId === item.id ? null : item.id
                            );
                          }}
                        >
                          <span>{item.function}</span>
                          <ChevronDown className="h-3 w-3" />
                        </button>
                        {openMenuProjectId === item.id &&
                          createPortal(
                            <div
                              className={
                                `fixed w-96 bg-white rounded-md shadow-lg border border-gray-200 z-[99999] ` +
                                (menuPlacement === "bottom"
                                  ? "mt-2"
                                  : "bottom-full mb-2")
                              }
                              onMouseDown={(e) => {
                                // Prevent the global mousedown outside handler from closing
                                // the menu before the anchor click can navigate
                                e.stopPropagation();
                              }}
                              style={
                                buttonPosition
                                  ? {
                                      left: `${buttonPosition.right - 410}px`, // 400px = w-96 + 16px offset
                                      top:
                                        menuPlacement === "bottom"
                                          ? `${buttonPosition.bottom + 0}px`
                                          : `${buttonPosition.top - 260}px`,
                                      backgroundColor: "white",
                                      zIndex: 99999,
                                    }
                                  : {}
                              }
                            >
                              <div className="py-1 bg-white">
                                {[
                                  {
                                    label: "Chi tiết",
                                    Icon: Eye,
                                    colorClass: "text-blue-600",
                                  },
                                  {
                                    label: "Sao chép",
                                    Icon: Copy,
                                    colorClass: "text-purple-600",
                                  },
                                  {
                                    label: "Sửa",
                                    Icon: Pencil,
                                    colorClass: "text-amber-600",
                                  },
                                  {
                                    label: "Xóa",
                                    Icon: Trash2,
                                    colorClass: "text-red-600",
                                  },
                                  {
                                    label:
                                      "Chọn Hội đồng xét duyệt thuyết minh đề cương",
                                    Icon: Users,
                                    colorClass: "text-teal-600",
                                  },
                                  {
                                    label: "Chọn Hội đồng thẩm định kinh phí",
                                    Icon: DollarSign,
                                    colorClass: "text-green-600",
                                  },
                                  {
                                    label: "Ký hợp đồng thực hiện",
                                    Icon: FileSignature,
                                    colorClass: "text-indigo-600",
                                  },
                                  {
                                    label: "Chuyển phụ trách",
                                    Icon: Share2,
                                    colorClass: "text-sky-600",
                                  },
                                ].map(({ label, Icon, colorClass }) =>
                                  label === "Chi tiết" ? (
                                    <button
                                      key={label}
                                      onClick={() => {
                                        localStorage.setItem(
                                          "projectListActive",
                                          "approval"
                                        );
                                        setOpenMenuProjectId(null);
                                        navigate(
                                          `/topics/approval/detail/${item.id}`
                                        );
                                      }}
                                      className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                    >
                                      <Icon
                                        className={`h-4 w-4 ${colorClass}`}
                                      />
                                      <span>{label}</span>
                                    </button>
                                  ) : label === "Sửa" ? (
                                    <button
                                      key={label}
                                      onClick={() => {
                                        localStorage.setItem(
                                          "projectListActive",
                                          "approval"
                                        );
                                        setOpenMenuProjectId(null);
                                        navigate(
                                          `/topics/approval/detail/${item.id}/edit`
                                        );
                                      }}
                                      className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                    >
                                      <Icon
                                        className={`h-4 w-4 ${colorClass}`}
                                      />
                                      <span>{label}</span>
                                    </button>
                                  ) : label === "Xóa" ? (
                                    <button
                                      key={label}
                                      onClick={() => {
                                        setDeleteProjectId(item.id);
                                        setShowDeleteConfirm(true);
                                        setOpenMenuProjectId(null);
                                      }}
                                      className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                    >
                                      <Icon
                                        className={`h-4 w-4 ${colorClass}`}
                                      />
                                      <span>{label}</span>
                                    </button>
                                  ) : label ===
                                    "Chọn Hội đồng xét duyệt thuyết minh đề cương" ? (
                                    <button
                                      key={label}
                                      onClick={() => {
                                        setOpenMenuProjectId(null);
                                        navigate(
                                          `/topics/approval/detail/${item.id}/council/select`
                                        );
                                      }}
                                      className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                    >
                                      <Icon
                                        className={`h-4 w-4 ${colorClass}`}
                                      />
                                      <span>{label}</span>
                                    </button>
                                  ) : label ===
                                    "Chọn Hội đồng thẩm định kinh phí" ? (
                                    <button
                                      key={label}
                                      onClick={() => {
                                        setOpenMenuProjectId(null);
                                        navigate(
                                          `/topics/approval/detail/${item.id}/council/funding`
                                        );
                                      }}
                                      className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                    >
                                      <Icon
                                        className={`h-4 w-4 ${colorClass}`}
                                      />
                                      <span>{label}</span>
                                    </button>
                                  ) : label === "Ký hợp đồng thực hiện" ? (
                                    <button
                                      key={label}
                                      onClick={() => {
                                        setOpenMenuProjectId(null);
                                        navigate(
                                          `/topics/approval/detail/${item.id}/contract/sign`
                                        );
                                      }}
                                      className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                    >
                                      <Icon
                                        className={`h-4 w-4 ${colorClass}`}
                                      />
                                      <span>{label}</span>
                                    </button>
                                  ) : label === "Chuyển phụ trách" ? (
                                    <button
                                      key={label}
                                      onClick={() => {
                                        setOpenMenuProjectId(null);
                                        navigate(
                                          `/topics/approval/detail/${item.id}/transfer`
                                        );
                                      }}
                                      className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                    >
                                      <Icon
                                        className={`h-4 w-4 ${colorClass}`}
                                      />
                                      <span>{label}</span>
                                    </button>
                                  ) : (
                                    <button
                                      key={label}
                                      onClick={() => {
                                        console.log(
                                          `Action: ${label} for project: ${item.id}`
                                        );
                                        setOpenMenuProjectId(null);
                                      }}
                                      className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                    >
                                      <Icon
                                        className={`h-4 w-4 ${colorClass}`}
                                      />
                                      <span>{label}</span>
                                    </button>
                                  )
                                )}
                              </div>
                            </div>,
                            document.body
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination inside the table container */}
          <div className="flex justify-between items-center p-4 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700">
              Hiển thị <span className="font-bold">1</span> đến{" "}
              <select className="font-bold text-blue-600 border-none bg-transparent focus:ring-0 focus:outline-none text-sm">
                <option>10</option>
                <option>20</option>
                <option>50</option>
                <option>100</option>
              </select>{" "}
              trong <span className="font-bold">10000</span> kết quả
            </div>
            <div className="flex space-x-2">
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &lt;
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                1
              </button>
              <button className="px-3 py-2 text-sm font-medium text-white bg-[#337ab7] border border-[#337ab7] rounded-md">
                2
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                3
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &gt;
              </button>
            </div>
          </div>
        </div>
      </div>
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-md p-4 max-w-sm w-full mx-4">
            <h3 className="text-base font-semibold text-gray-900 mb-2">
              Xác nhận xóa
            </h3>
            <p className="text-xs text-gray-600 mb-3">
              Bạn có chắc muốn xóa đề tài ?
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteProjectId(null);
                }}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                onClick={() => {
                  console.log("Delete project", deleteProjectId);
                  setShowDeleteConfirm(false);
                  setDeleteProjectId(null);
                }}
                className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700"
              >
                Xóa
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default TopicApproval;
